import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { Button, Empty, Spin, Table } from 'antd-v5';
import { PlusOutlined } from '@ant-design/icons';
import { Section } from '../../components/Section';
import AddRolexInquiryModal from '../../../../Rolex/AddRolexInquiryModal';
import { listInquiries } from '../../../../../api/vendors';
import { transformInquiry } from '../../../../../components/Routes/Rolex/utils';
import { getMerchantTags } from '../../../../../services/clients';

const ROLEX_VENDOR_ID = 686;

const HeaderCell = props => (
  <th
    {...props}
    className="header-cell-no-separator"
    style={{ ...props.style, backgroundColor: 'transparent', color: '#575757' }}
  />
);

const TodayRolexInquiries = ({ auth }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingInquiry, setEditingInquiry] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tagLookup, setTagLookup] = useState([]);

  useEffect(() => {
    getMerchantTags({ query: { limit: 500 }, loadingStatusOff: true })
      .then(response => setTagLookup(response?.results || []))
      .catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listInquiries({
        vendorKey: 'rolex',
        associateId: auth?.userId,
        statuses: 'WAITING,ORDERED',
        limit: 5,
      });
      const stores = auth?.stores || [];
      const items = (response?.data || []).map(item => transformInquiry(item, { stores, tags: tagLookup }));
      setData(items);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [auth?.userId, auth?.stores, tagLookup]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'clientName',
      key: 'clientName',
      render: (text, record) => <Link to={`/Clients?client=${record.clientId}&clientTab=rolex`}>{text}</Link>,
    },
    {
      title: 'Inquiry Date',
      dataIndex: 'inquiryDate',
      key: 'inquiryDate',
      render: (text, record) => (
        <div>
          <div>{text}</div>
          <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.5)' }}>{record.durationMonths}</div>
        </div>
      ),
    },
    {
      title: 'Last Inbound',
      dataIndex: 'lastInbound',
      key: 'lastInbound',
      render: (text, record) => (
        <div>
          <div>{text}</div>
          <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.5)' }}>{record.lastInboundDuration}</div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: value => {
        const label = { WAITING: 'Waiting', ORDERED: 'Ordered' };
        return label[value] || value;
      },
    },
    {
      title: 'Product',
      dataIndex: 'productName',
      key: 'productName',
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {record.productImage && (
            <img
              src={record.productImage}
              alt={record.productMpn}
              style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 4, flexShrink: 0 }}
            />
          )}
          <div>
            <div>{text}</div>
            <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.5)' }}>
              {record.productMpn}
              {record.productCount > 1 && (
                <span style={{ color: 'rgba(0,0,0,0.35)', fontStyle: 'italic' }}>
                  {' '}
                  +{record.productCount - 1} more
                </span>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <span style={{ color: '#1677ff', cursor: 'pointer' }} onClick={() => setEditingInquiry(record)}>
          Edit
        </span>
      ),
    },
  ];

  const headerActions = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <Button
        size="small"
        icon={<PlusOutlined style={{ color: '#fff', fontSize: 14 }} />}
        style={{
          borderRadius: 6,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#33AAFC',
          border: 'none',
        }}
        onClick={() => setShowAddModal(true)}
        title="Add Inquiry"
      />
      <Link to="/all-rolex-inquiries">
        <Button type="link" className="cb-link-btn">
          View All
        </Button>
      </Link>
    </div>
  );

  return (
    <>
      <Section title="Rolex Inquiries" className="m-btm-35" actions={headerActions}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            pagination={false}
            size="middle"
            bordered={false}
            className="today-table"
            components={{ header: { cell: HeaderCell } }}
            onHeaderRow={() => ({
              className: 'today-table',
              style: {
                background: 'transparent',
                color: '#575757',
                fontWeight: 600,
                fontSize: '13px',
                textAlign: 'left',
              },
            })}
            locale={{
              emptyText: (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No open Rolex inquiries assigned to you" />
              ),
            }}
          />
        )}
      </Section>

      <AddRolexInquiryModal
        open={showAddModal || !!editingInquiry}
        onCancel={() => {
          setShowAddModal(false);
          setEditingInquiry(null);
        }}
        onSave={() => {
          setShowAddModal(false);
          setEditingInquiry(null);
          fetchData();
        }}
        inquiry={editingInquiry}
        vendorId={ROLEX_VENDOR_ID}
      />
    </>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(TodayRolexInquiries);
